import { DirectReportDto, ProductVisibility } from "@coaching-culture/types";
import { Loader, Select, typeSize } from "@coaching-culture/ui";
import { useProductVisibility } from "hooks/useProductVisibility";
import { sortBy } from "lodash";
import AddRelationshipModal from "pages/Solutions/AddRelationshipModal";
import { ConversationCommentsModal } from "pages/Solutions/ConversationCommentsModal";
import { DevelopmentNotesModal } from "pages/Solutions/DevelopmentNotesModal";
import { useTeam } from "queries/performance";
import { useState } from "react";
import styled from "styled-components";
import { AddTeamMemberButton } from "./AddTeamMemberButton";
import { nextConvo, useTeamRagThresholds } from "./metrics";
import { cardStatuses, TeamDrawer, worstStatusRank } from "./nextStep";
import { TeamViewCard } from "./TeamViewCard";
import { TeamViewModal } from "./TeamViewModal";

type Filter = "all" | "attention" | "awaiting";
type Sort = "attention" | "name" | "next";

const sortOptions: { value: Sort; label: string }[] = [
  { value: "attention", label: "Needs attention first" },
  { value: "name", label: "Name" },
  { value: "next", label: "Next conversation" },
];

const Toolbar = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 18px;
`;

const Chips = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const Chip = styled.button<{ active: boolean }>`
  min-height: 36px;
  padding: 0 14px;
  border-radius: 18px;
  font-family: inherit;
  font-size: ${typeSize("small")};
  font-weight: ${(p) => (p.active ? 600 : 500)};
  cursor: pointer;
  border: 1px solid
    ${(p) => (p.active ? p.theme.colors.black : p.theme.colors.borderStrong)};
  background: ${(p) =>
    p.active ? p.theme.colors.black : p.theme.colors.surface};
  color: ${(p) => (p.active ? p.theme.colors.surface : p.theme.colors.body)};

  &:focus-visible {
    outline: 2px solid ${(p) => p.theme.colors.primary};
    outline-offset: 2px;
  }
`;

const SortControl = styled.div`
  margin-left: auto;
  display: flex;
  gap: 8px;
  align-items: center;

  & > label {
    font-size: ${typeSize("small")};
    color: ${(p) => p.theme.colors.textMuted};
  }
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 16px;
  align-items: stretch;
  width: 100%;
`;

const isConfirmed = (x: DirectReportDto) =>
  x.relationship.toAccepted != null && x.relationship.fromAccepted != null;

export const TeamView = () => {
  const team = useTeam();
  const thresholds = useTeamRagThresholds();
  const hasMeasures =
    useProductVisibility("measures") === ProductVisibility.Enabled;
  const [active, setActive] = useState<{
    id: string;
    drawer?: TeamDrawer;
  } | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [viewPrivateNotes, setViewPrivateNotes] = useState("");
  const [viewDevNotes, setViewDevNotes] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [sort, setSort] = useState<Sort>("attention");

  if (team.isLoading) {
    return <Loader />;
  }

  const reports = team.data.directReports;
  const activeUser = reports.find((x) => x.id === active?.id);

  const rank = (x: DirectReportDto) =>
    isConfirmed(x)
      ? worstStatusRank(cardStatuses(x, thresholds, hasMeasures))
      : -1;

  const needsAttention = reports.filter((x) => rank(x) === 2);
  const awaiting = reports.filter((x) => !isConfirmed(x));

  const filtered =
    filter === "attention"
      ? needsAttention
      : filter === "awaiting"
        ? awaiting
        : reports;

  // Unconfirmed relationships always go last.
  const sorted = sortBy(filtered, [
    (x) => (isConfirmed(x) ? 0 : 1),
    (x) =>
      sort === "attention"
        ? -rank(x)
        : sort === "next"
          ? (nextConvo(x)?.scheduledDate ?? Number.MAX_SAFE_INTEGER)
          : 0,
    (x) => x.name.toLowerCase(),
  ]);

  const chips: { value: Filter; label: string; count: number }[] = [
    { value: "all", label: "All", count: reports.length },
    {
      value: "attention",
      label: "Needs attention",
      count: needsAttention.length,
    },
    { value: "awaiting", label: "Awaiting acceptance", count: awaiting.length },
  ];

  return (
    <>
      {active != null && activeUser != null && (
        <TeamViewModal
          user={activeUser}
          initialDrawer={active.drawer}
          onClose={() => setActive(null)}
          onViewPrivateNotes={() => setViewPrivateNotes(activeUser.id)}
          onViewDevelopmentNotes={() =>
            setViewDevNotes(activeUser.relationship.id)
          }
        />
      )}
      {viewPrivateNotes !== "" && (
        <ConversationCommentsModal
          userId={viewPrivateNotes}
          onCancel={() => setViewPrivateNotes("")}
        />
      )}
      {viewDevNotes !== null && (
        <DevelopmentNotesModal
          relationshipId={viewDevNotes}
          onCancel={() => setViewDevNotes(null)}
        />
      )}
      {showModal && (
        <AddRelationshipModal
          onClose={(newRelationship) => {
            if (newRelationship) {
              team.refetch();
            }
            setShowModal(false);
          }}
        />
      )}
      {reports.length > 0 && (
        <Toolbar>
          <Chips>
            {chips
              .filter((c) => c.value === "all" || c.count > 0)
              .map((c) => (
                <Chip
                  key={c.value}
                  type="button"
                  active={filter === c.value}
                  aria-pressed={filter === c.value}
                  onClick={() => setFilter(c.value)}
                >
                  {c.label} · {c.count}
                </Chip>
              ))}
          </Chips>
          <SortControl>
            <label htmlFor="team-sort">Sort</label>
            <Select
              id="team-sort"
              width={220}
              value={sort}
              options={sortOptions}
              onChange={(ev) => setSort(ev.target.value as Sort)}
            />
          </SortControl>
        </Toolbar>
      )}
      <CardGrid>
        {sorted.map((x) => (
          <TeamViewCard
            key={x.id}
            user={x}
            onSelect={(drawer) => setActive({ id: x.id, drawer })}
            onViewPrivateNotes={() => setViewPrivateNotes(x.id)}
            onViewDevelopmentNotes={() => setViewDevNotes(x.relationship.id)}
          />
        ))}
        {filter === "all" && (
          <AddTeamMemberButton onClick={() => setShowModal(true)} />
        )}
      </CardGrid>
    </>
  );
};
